import {
  BackButtonCWUI,
  CustomerCategoryTitleUI,
} from '@next-order/next-order-components';
import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { getOrdersAPI } from './actions';
import { selectStoreConfig } from 'containers/Layout/selectors';
import {
  setState as setPropsState,
  toggleMobileCart,
} from 'containers/Home/actions';
import { engineAddItemsToOrder } from 'helpers/order-engine';
import { getMenuItemForInstore } from 'helpers/getMenuItemForInstore';
import AnimationWrapper from 'components/AnimationWrapper';
import LottieAnimation from 'components/Lottie';
import getImage from 'helpers/getImage';
import orderTemplate from './orderTemplate';
import OrderView from './OrderView';
import { ReactComponent as OrderDetailsIcon } from '../../assets/images/order-details.svg';
import { ReactComponent as OrderPrintIcon } from '../../assets/images/order-print.svg';
import { ReactComponent as OrderCounterClockwiseIcon } from '../../assets/images/arrow-counterclockwise.svg';

import {
  formatDate,
  formatPrice,
  getOrderStatusColor,
  getOrderStatusText,
  getOrderTypeText,
  getOrderTypeStyle,
  getPaymentMethodText,
  getCardUrl,
} from './utils';
import { onClickDefaultState } from 'components/Header/FloatingNavbarMobile';

export default function Orders({ userDetails, onBack }) {
  const storeConfig = useSelector(selectStoreConfig);
  const { storeId, currency } = storeConfig;
  const dispatch = useDispatch();

  const {
    currentOrder,
    orderSetup,
    allSpecials,
    halfHalfSetup,
    offers,
    publicHolidays,
    menuItems,
    menuItemSizes,
    menuItemSubModifiers,
    isMinifiedVersion,
  } = useSelector((state) => state.homeReducer);
  const { userDetails: currentUser } = useSelector(
    (state) => state.homeReducer
  );

  const [orders, setOrders] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [viewMode, setViewMode] = useState('list'); // 'list' or 'details'
  const [expandedOrders, setExpandedOrders] = useState(new Set()); // Track which orders are expanded
  const [showScrollShadow, setShowScrollShadow] = useState(false);
  const [failedImages, setFailedImages] = useState(new Set()); // Track failed image loads
  const [reorderingOrderId, setReorderingOrderId] = useState(null); // Track which order is being reordered

  useEffect(() => {
    const fetchOrders = async () => {
      try {
        setIsLoading(true);
        setError('');

        const response = await getOrdersAPI({
          storeId,
          userId: userDetails._id,
          limit: '50', // Get more orders for history
        });
        setOrders(response?.data?.orders || []);
      } catch (err) {
        console.error('Error fetching orders:', err);
        setError(err.message || 'Failed to load orders. Please try again.');
      } finally {
        setIsLoading(false);
      }
    };

    if (userDetails?._id && storeId) {
      fetchOrders();
    }
  }, [userDetails?._id, storeId]);

  const printPDF = (order) => {
    var printWindow = window.open('', '_blank');
    if (printWindow) {
      printWindow.document.write(orderTemplate(order, storeConfig));
      printWindow.document.close();
      printWindow.print();
    } else {
      console.error('Print window is not defined');
    }
  };

  const renderOrderItem = (item, index, isLastVisibleItem = false) => {
    // Get the selected size name
    const sizeName = item.selectedSizes?.[0]?.name || '';

    // Get modifier names (excluding size modifiers)
    const modifierNames =
      item.selectedModifiers
        ?.filter((mod) => mod.name !== 'size')
        ?.flatMap(
          (mod) =>
            mod.subModifiers
              ?.filter((sub) => sub.isSelected)
              ?.map((sub) => sub.name) || []
        ) || [];

    // Get variant names (like gluten-free base)
    const variantNames =
      item.selectedSizes?.[0]?.variants
        ?.filter((variant) => variant.isSelected)
        ?.map((variant) => variant.name) || [];

    const allExtras = [...modifierNames, ...variantNames].filter(Boolean);

    // Get item image
    const itemImage = getImage(item.mediaPath || item.urlS3);

    // Create unique key for this item's image
    const imageKey = `${item._id || item.id || index}-${itemImage}`;
    const imageHasFailed = failedImages.has(imageKey);
    const shouldShowImage = itemImage && !imageHasFailed;

    const handleImageError = () => {
      setFailedImages((prev) => new Set([...prev, imageKey]));
    };

    return (
      <div
        key={index}
        className={`flex items-center py-3 ${
          shouldShowImage ? 'gap-3' : 'gap-0'
        } ${!isLastVisibleItem ? 'border-b border-gray-100' : ''}`}
      >
        {/* Item Image */}
        {shouldShowImage && (
          <div className='flex-shrink-0'>
            <img
              src={itemImage}
              alt={item.name}
              className='w-12 h-12 object-cover rounded-lg'
              onError={handleImageError}
            />
          </div>
        )}

        {/* Item Details */}
        <div className='flex-1 flex flex-col gap-2 min-w-0'>
          <div className='text-sm font-medium text-gray-900 truncate'>
            {item.name}
          </div>
          {sizeName && (
            <div className='text-xs text-gray-500'>Size: {sizeName}</div>
          )}
          {allExtras.length > 0 && (
            <div className='text-xs text-gray-500 truncate'>
              {allExtras.join(', ')}
            </div>
          )}
          {item.itemNote && (
            <div className='text-xs text-gray-500 italic truncate'>
              Note: {item.itemNote}
            </div>
          )}
        </div>

        {/* Quantity and Price */}
        <div className='text-right flex-shrink-0'>
          <div className='text-sm font-medium text-gray-900'>
            Quantity: {item.quantity}
          </div>
        </div>
      </div>
    );
  };

  const handleViewOrder = (order) => {
    setSelectedOrder(order);
    setViewMode('details');
  };

  const handleBackToList = () => {
    setSelectedOrder(null);
    setViewMode('list');
  };

  const toggleOrderExpansion = (orderId) => {
    setExpandedOrders((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(orderId)) {
        newSet.delete(orderId);
      } else {
        newSet.add(orderId);
      }
      return newSet;
    });
  };

  const handleScroll = (e) => {
    const container = e.target;
    const { scrollTop, scrollHeight, clientHeight } = container;

    // Show shadow when there's scrollable content and user has scrolled
    const isScrollable = scrollHeight > clientHeight;
    const hasScrolled = scrollTop > 5;

    setShowScrollShadow(isScrollable && hasScrolled);
  };

  const getItemPriceForReorder = (item, halfHalfSetup) => {
    let itemPrice = 0;
    let quantity = 0;
    let extraPrice = 0;

    if (!item.itemType || item.itemType === 0) {
      item?.selectedSizes?.forEach((size) => {
        if (size.quantity > 0) {
          quantity = quantity + size.quantity;
          itemPrice = itemPrice + size.quantity * Number(size.price);
        }

        size.variants?.forEach((v) => {
          if (v.quantity && Number(v.quantity) > 0)
            itemPrice = itemPrice + size.quantity * Number(v.price);
        });
      });

      if (quantity > 0) {
        item?.selectedExtraIngredients?.forEach((extra) => {
          if (extra.quantity > 0) {
            extraPrice = extraPrice + extra.quantity * Number(extra.price);
          }
        });
        itemPrice = extraPrice * quantity + itemPrice;
      }
    } else if (Array.isArray(item.selectedModifiers)) {
      let selectedSubModifier = { _id: 0 };
      item.selectedModifiers.forEach((m) => {
        if (m._id === 'xxx-xxx-xxx' && m.subModifiers.length > 0) {
          selectedSubModifier = m.subModifiers[0];
          quantity = m.subModifiers[0].quantity;
        }
      });
      if (item.selectedModifiers && selectedSubModifier._id !== 0) {
        item.selectedModifiers.forEach((m) => {
          if (m._id !== 'xxx-xxx-xxx') {
            m.subModifiers.forEach((s) => {
              if (s._id === selectedSubModifier._id && Number(s.quantity) > 0) {
                itemPrice = itemPrice + Number(s.quantity) * Number(s.price);
              } else if (Number(s.quantity) > 0) {
                let matchedPrices = s.prices
                  ? s.prices.filter(
                      (f) =>
                        f._id === selectedSubModifier._id &&
                        f._vId === selectedSubModifier._vId
                    )
                  : [];
                if (matchedPrices.length > 0) {
                  let matchedPrice = matchedPrices[0];
                  itemPrice =
                    itemPrice + Number(s.quantity) * Number(matchedPrice.price);
                }
              }
            });
          }
        });
      }
    }

    return { itemPrice, extraPrice };
  };

  const handleReorder = async (order) => {
    if (!order.menuItems || order.menuItems.length === 0) {
      console.error('No menu items found in order');
      return;
    }

    setReorderingOrderId(order.id);

    try {
      const itemsToAdd = order.menuItems.map((item) => {
        const latestMenuItem = menuItems.find(
          (menuItem) => menuItem._id === item._id
        );

        if (!latestMenuItem) {
          console.warn(`Menu item ${item._id} not found in current menu items`);
          return item;
        }

        const newMenuItemSizes = menuItemSizes.filter(
          (f) => f.menuItemId === item._id
        );
        const newMenuItemSubModifiers = menuItemSubModifiers.filter(
          (f) => f.menuItemId === item._id
        );

        const updatedMenuItem = getMenuItemForInstore(
          latestMenuItem,
          newMenuItemSizes,
          newMenuItemSubModifiers
        );

        const cleanItem = {
          ...updatedMenuItem,
          selectedSizes:
            item.selectedSizes?.map((size) => {
              const updatedSize = updatedMenuItem.selectedSizes?.find(
                (s) => s._id === size._id
              );

              let updatedVariants = null;
              if (size.variants && updatedSize?.variants) {
                updatedVariants = size.variants.map((variant) => {
                  const updatedVariant = updatedSize.variants.find(
                    (v) => v._id === variant._id
                  );
                  return {
                    ...updatedVariant,
                    ...variant,
                    price: String(updatedVariant?.price || variant.price || 0),
                  };
                });
              }

              return {
                ...updatedSize,
                ...size,
                price: String(updatedSize?.price || size.price || 0),
                variants: updatedVariants || size.variants || null,
                quantity: size.quantity || 1,
              };
            }) || [],
          selectedModifiers:
            item.selectedModifiers?.map((modifier) => {
              const updatedModifier = updatedMenuItem.selectedModifiers?.find(
                (m) => m._id === modifier._id
              );
              const newUpdatedModifier = { ...updatedModifier, ...modifier };
              if (!newUpdatedModifier.price) {
                delete newUpdatedModifier.price;
              }
              if (!newUpdatedModifier.prices) {
                delete newUpdatedModifier.prices;
              }
              if (!newUpdatedModifier.quantity) {
                delete newUpdatedModifier.quantity;
              }
              return {
                ...newUpdatedModifier,
                subModifiers:
                  modifier.subModifiers?.map((subMod) => {
                    const updatedSubMod = updatedModifier?.subModifiers?.find(
                      (s) => s._id === subMod._id
                    );

                    let updatedSubModVariants = null;
                    if (subMod.variants && updatedSubMod?.variants) {
                      updatedSubModVariants = subMod.variants.map((variant) => {
                        const updatedVariant = updatedSubMod.variants.find(
                          (v) => v._id === variant._id
                        );
                        return {
                          ...updatedVariant,
                          ...variant,
                          price: String(
                            updatedVariant?.price || variant.price || 0
                          ),
                        };
                      });
                    }

                    return {
                      ...updatedSubMod,
                      ...subMod,
                      price: String(updatedSubMod?.price || subMod.price || 0),
                      variants:
                        updatedSubModVariants || subMod.variants || null,
                      quantity: subMod.quantity || 1,
                    };
                  }) || [],
              };
            }) || [],
          selectedIngredients:
            item.selectedIngredients?.map((ingredient) => ({
              ...ingredient,
              inOrder: ingredient.inOrder !== false,
            })) || [],
          selectedExtraIngredients:
            item.selectedExtraIngredients?.map((extra) => {
              const updatedExtra =
                updatedMenuItem.selectedExtraIngredients?.find(
                  (e) => e._id === extra._id
                );
              let currentPrice = extra.price || 0;

              if (updatedExtra?.prices) {
                const selectedSizeId = extra.sizeId;
                const matchedPrice = updatedExtra.prices.find(
                  (priceObj) =>
                    priceObj._id === selectedSizeId &&
                    priceObj.isSelected === true
                );

                if (matchedPrice) {
                  currentPrice = Number(matchedPrice.price) || 0;

                  if (matchedPrice.variants) {
                    matchedPrice.variants.forEach((variant) => {
                      if (variant.isSelected) {
                        currentPrice += Number(variant.price) || 0;
                      }
                    });
                  }
                } else {
                  currentPrice = Number(updatedExtra.prices[0]?.price) || 0;
                }
              }
              return {
                ...extra,
                price: String(currentPrice),
                quantity: extra.quantity || 1,
              };
            }) || [],
        };

        cleanItem.quantity = item.quantity || 1;
        cleanItem.removedIngredients = item.removedIngredients || [];
        if (item.isHalf) {
          cleanItem.isHalf = true;
          cleanItem.whichHalf = item.whichHalf;
          cleanItem.halfIndex = item.halfIndex;
          cleanItem.isSecondHalf = item.isSecondHalf;
        }

        const { itemPrice, extraPrice } = getItemPriceForReorder(
          cleanItem,
          halfHalfSetup
        );

        delete cleanItem.isSpecialItem;
        delete cleanItem.specialId;

        cleanItem.itemPrice = itemPrice - extraPrice;
        cleanItem.extraPrice = extraPrice;
        cleanItem.payablePrice = itemPrice;

        return cleanItem;
      });

      const props = {
        currentOrder,
        orderSetup,
        allSpecials,
        halfHalfSetup,
        offers,
        publicHolidays,
        storeConfig,
        userDetails: currentUser,
      };

      const { currentOd } = engineAddItemsToOrder(itemsToAdd, props);

      dispatch(toggleMobileCart(true));
      dispatch(
        setPropsState({ currentOrder: currentOd, ...onClickDefaultState })
      );
    } catch (error) {
      console.error('Error reordering items:', error);
      alert('Failed to add items to cart. Please try again.');
    } finally {
      setReorderingOrderId(null);
    }
  };

  const renderOrder = (order) => {
    const isExpanded = expandedOrders.has(order.id);
    const hasMoreItems = order.menuItems?.length > 2;
    const itemsToShow = isExpanded
      ? order.menuItems
      : order.menuItems?.slice(0, 2);
    const isReordering = reorderingOrderId === order.id;

    return (
      <div
        key={order.id}
        className='bg-white border border-gray-200 rounded-lg p-4 mb-4 shadow-sm'
      >
        <div className='flex justify-between items-start mb-3'>
          <div className='space-y-2'>
            <div className='flex items-center gap-2'>
              <div className='text-lg font-semibold text-gray-900'>
                Order #{order.deliveryCode}
              </div>
              <span
                className={`capitalize font-medium px-2 py-1 rounded-md text-xs ${getOrderTypeStyle(
                  order.orderType
                )}`}
              >
                {getOrderTypeText(order.orderType)}
              </span>
            </div>
            <div className='text-sm text-gray-500'>
              {formatDate(order.dateTimestamp)}
            </div>
            {order.paymentType && (
              <div className='text-xs text-gray-500 font-medium flex items-center gap-1'>
                Payment:
                {order.paymentType === '0' ? (
                  <>
                    <i
                      className='icon-Cash'
                      style={{
                        color: 'rgb(40, 105, 43)',
                        fontSize: '24px',
                        position: 'relative',
                      }}
                    />
                    <span>{getPaymentMethodText(order.paymentType)}</span>
                  </>
                ) : (
                  <>
                    {order.cardType && (
                      <img
                        src={getCardUrl(order.cardType)}
                        alt={getPaymentMethodText(order.paymentType)}
                        className='w-6 h-4 object-contain'
                      />
                    )}
                    <span>{getPaymentMethodText(order.paymentType)}</span>
                  </>
                )}
              </div>
            )}
          </div>
          <div className='text-right space-y-1'>
            <div className='text-lg font-semibold text-gray-900'>
              {formatPrice(order.payableAmount, currency)}
            </div>
            <div
              className={`text-sm font-medium capitalize ${getOrderStatusColor(
                order.orderStatus
              )}`}
            >
              {getOrderStatusText(order.orderStatus)}
            </div>
          </div>
        </div>

        <div className='border-t border-gray-100 pt-3'>
          <div className='text-sm font-medium text-gray-700 mb-2'>
            <div className='flex items-center gap-2'>
              <span>Items</span>
              <span className='bg-green-200 text-gray-700 px-2 py-1 rounded-full text-xs font-medium'>
                {order.menuItems?.length || 0}
              </span>
            </div>
          </div>
          <div className='space-y-1'>
            {itemsToShow?.map((item, index) => {
              const isLastVisibleItem =
                index === itemsToShow.length - 1 &&
                (hasMoreItems || itemsToShow.length < 2);
              return renderOrderItem(item, index, isLastVisibleItem);
            })}
            {hasMoreItems && (
              <div className='pt-2 flex justify-center'>
                <button
                  onClick={() => toggleOrderExpansion(order.id)}
                  className='flex items-center gap-1 px-3 py-2 text-sm text-gray-600 border border-gray-200 hover:bg-gray-100 rounded-md transition-colors duration-200'
                >
                  <span>
                    {isExpanded
                      ? `Show less`
                      : `${order.menuItems.length - 2} more items`}
                  </span>
                  <svg
                    className={`w-3 h-3 transition-transform duration-200 ${
                      isExpanded ? 'rotate-180' : ''
                    }`}
                    fill='none'
                    stroke='currentColor'
                    viewBox='0 0 24 24'
                  >
                    <path
                      strokeLinecap='round'
                      strokeLinejoin='round'
                      strokeWidth={2}
                      d='M19 9l-7 7-7-7'
                    />
                  </svg>
                </button>
              </div>
            )}
          </div>
        </div>

        {((order.deliveryDate && order.orderType === '2') ||
          (order.pickupTime && order.orderType === '1') ||
          order.notes) && (
          <div className='mt-3 pt-2 border-t border-gray-100 space-y-1'>
            {order.deliveryDate && order.orderType === '2' && (
              <div className='text-xs text-gray-500'>
                <span className='font-bold'>Delivery Time:</span>{' '}
                {formatDate(order.deliveryDate)}
              </div>
            )}
            {order.pickupTime && order.orderType === '1' && (
              <div className='text-xs text-gray-500'>
                <span className='font-bold'>Pickup Time:</span>{' '}
                {formatDate(order.pickupTime)}
              </div>
            )}
            {order.notes && (
              <div className='text-xs text-gray-500'>
                <span className='font-bold'>Notes:</span> {order.notes}
              </div>
            )}
            {/* {order.voucherName && (
            <div className="text-xs text-green-600">
              Voucher: <span className="font-medium">{order.voucherName}</span>
            </div>
          )} */}
          </div>
        )}

        <div className='mt-2 pt-4 border-t border-gray-100 space-y-3'>
          <div className='flex justify-between gap-4'>
            <button
              className='border border-gray-300 hover:bg-gray-50 text-gray-700 text-sm font-medium py-2 px-4 rounded-lg transition-colors duration-200 flex items-center gap-2'
              onClick={() => handleViewOrder(order)}
            >
              <OrderDetailsIcon className='w-5 h-5' />
              View Details
            </button>
            <button
              className='border border-gray-300 hover:bg-gray-50 text-gray-700 text-sm font-medium py-2 px-4 rounded-lg transition-colors duration-200 flex items-center gap-2'
              onClick={() => printPDF(order)}
            >
              <OrderPrintIcon className='w-4 h-4' />
              View Receipt
            </button>
          </div>
          {order.menuItems && order.menuItems.length > 0 && (
            <button
              className={`w-full border border-green-500 text-sm font-medium py-2 px-4 rounded-lg transition-colors duration-200 flex items-center justify-center gap-2 ${
                isMinifiedVersion || isReordering
                  ? 'opacity-50 cursor-not-allowed bg-gray-100 text-gray-500 !border-gray-300'
                  : 'hover:bg-green-50 text-green-700'
              }`}
              onClick={() =>
                !isMinifiedVersion && !isReordering && handleReorder(order)
              }
              disabled={isMinifiedVersion || isReordering}
            >
              {isReordering ? (
                <div className='w-4 h-4 border-2 border-green-500 border-t-transparent rounded-full animate-spin'></div>
              ) : (
                <OrderCounterClockwiseIcon className='w-5 h-5' />
              )}
              {isMinifiedVersion
                ? 'Loading Re-order...'
                : isReordering
                ? 'Adding Items to Cart...'
                : 'Re-order'}
            </button>
          )}
        </div>
      </div>
    );
  };

  // If in details view, render OrderView component
  if (viewMode === 'details' && selectedOrder) {
    return <OrderView orderObj={selectedOrder} onBack={handleBackToList} />;
  }

  return (
    <AnimationWrapper
      animation='fade-in'
      style={{
        width: '100%',
        height: '100%',
      }}
      className='profile-section'
    >
      <aside className='sidebar'>
        <div className='sidebar__wrapper'>
          <div className='card__about'>
            <div className='relative h-screen pt-4'>
              <BackButtonCWUI onClick={onBack} extendedClassName='mx-5' />

              <div className='relative pb-4'>
                <CustomerCategoryTitleUI extendedClassName='mt-3.25 !mb-0 ml-[-1px] px-5'>
                  Order History
                </CustomerCategoryTitleUI>

                {/* Shadow below title when scrolled */}
                <div
                  className={`absolute left-0 right-0 top-full h-3 pointer-events-none transition-opacity duration-200 z-10 ${
                    showScrollShadow ? 'opacity-100' : 'opacity-0'
                  }`}
                  style={{
                    background:
                      'linear-gradient(to bottom, rgba(0, 0, 0, 0.1), transparent)',
                  }}
                />
              </div>

              {/* Scrollable Orders Container */}
              <div
                className='overflow-y-auto pb-20 h-[calc(100vh-200px)] md:h-[calc(100vh-150px)] mt-0 px-5'
                onScroll={handleScroll}
              >
                {isLoading ? (
                  <div className='flex justify-center items-center h-[60vh] text-xs'>
                    <LottieAnimation statusText='' />
                  </div>
                ) : error ? (
                  <div className='bg-red-50 border border-red-200 rounded-lg p-4 text-red-700'>
                    {error}
                  </div>
                ) : orders.length === 0 ? (
                  <div className='text-center py-12'>
                    <div className='text-gray-500 text-lg mb-2'>
                      No orders found
                    </div>
                    <div className='text-gray-400 text-sm'>
                      Your order history will appear here once you place your
                      first order.
                    </div>
                  </div>
                ) : (
                  <div>
                    <div className='text-sm text-gray-600 mb-4'>
                      Showing {orders.length} Order
                      {orders.length !== 1 ? 's' : ''}
                    </div>
                    {orders.map(renderOrder)}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </aside>
    </AnimationWrapper>
  );
}
